/**
 * Indicator Shape Primitives
 *
 * Canvas-backed lightweight-charts primitives for indicator visuals that a
 * plain line / histogram series cannot express:
 *
 *  1. **Curve bands** — a shaded channel whose upper and lower edges both move
 *     with time, and whose fill colour switches from one horizontal segment to
 *     the next (the TradingView "EMA band" look). `IndicatorBand` only supports
 *     flat rectangles, which is enough for support/resistance boxes but not for
 *     an envelope that breathes with every bar.
 *  2. **Reference segments** — bounded horizontal dotted lines (the stored
 *     previous swing peak / trough a breakout has to clear) that end at a
 *     specific bar instead of running to the right edge of the chart.
 *  3. **Boxed labels** — solid rounded rectangles with centred bold text
 *     (LONG / SHORT), matching TradingView's `label.new` style. The marker
 *     plugin can only draw circles/squares/arrows with side text, so these are
 *     painted directly.
 *
 * Everything is batched into a single primitive attached to the candle series
 * so one canvas pass draws every indicator's shapes. Bands and reference
 * segments are pushed to the `bottom` z-layer (behind the candles, like
 * TradingView's `fill` drawings) while labels sit on the `top` layer.
 */

import { IChartApi, UTCTimestamp, ISeriesPrimitive } from "lightweight-charts";
import {
  IndicatorChartLabel,
  IndicatorCurveBand,
  IndicatorLineSegment,
} from "../types/indicator.types";
import { shade } from "../color-utils";

// ─── Resolved shapes (indicator output + the chart's IST offset) ──────────────
interface CurveBandShape extends IndicatorCurveBand { istOffset: number }
interface SegmentShape extends IndicatorLineSegment { istOffset: number }
interface LabelShape extends IndicatorChartLabel { istOffset: number }

export interface IndicatorShapeData {
  curveBands: CurveBandShape[];
  segments: SegmentShape[];
  labels: LabelShape[];
}

export const EMPTY_SHAPE_DATA: IndicatorShapeData = {
  curveBands: [],
  segments: [],
  labels: [],
};

/** Index of the first entry with `arr[i] >= value`. */
function lowerBound(arr: Float64Array, n: number, value: number): number {
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Index of the last entry with `arr[i] <= value`. */
function upperBound(arr: Float64Array, n: number, value: number): number {
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] <= value) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

/** Keep a box of `w` px fully inside a `width` px pane, with a small margin. */
function clampLeft(left: number, w: number, width: number): number {
  const margin = 2;
  if (w + margin * 2 >= width) return margin;
  if (left < margin) return margin;
  if (left + w > width - margin) return width - margin - w;
  return left;
}

/** Rounded rectangle path with a manual fallback for older canvas impls. */
function roundRectPath(ctx: any, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, radius);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

// ─── Renderer ─────────────────────────────────────────────────────────────────
class IndicatorShapeRenderer {
  private _data: IndicatorShapeData = EMPTY_SHAPE_DATA;
  private _chart: IChartApi;
  private _series: any;

  constructor(data: IndicatorShapeData, chart: IChartApi, series: any) {
    this._data = data;
    this._chart = chart;
    this._series = series;
  }

  update(data: IndicatorShapeData) {
    this._data = data || EMPTY_SHAPE_DATA;
  }

  private _x(time: number, istOffset: number): number | null {
    const c = this._chart.timeScale().timeToCoordinate((time + istOffset) as UTCTimestamp);
    return c === null || c === undefined || Number.isNaN(c) ? null : c;
  }

  private _y(price: number): number | null {
    const c = this._series.priceToCoordinate(price);
    return c === null || c === undefined || Number.isNaN(c) ? null : c;
  }

  /** Bands + reference segments — painted behind the candles. */
  drawBackground(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const {
        context: ctx,
        bitmapSize,
        horizontalPixelRatio: hpr,
        verticalPixelRatio: vpr,
      } = scope;
      this._drawCurveBands(ctx, bitmapSize.width, bitmapSize.height, hpr, vpr);
      this._drawSegments(ctx, bitmapSize.width, bitmapSize.height, hpr, vpr);
    });
  }

  /** Boxed labels — painted on top of everything. */
  drawForeground(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const {
        context: ctx,
        bitmapSize,
        horizontalPixelRatio: hpr,
        verticalPixelRatio: vpr,
      } = scope;
      this._drawLabels(ctx, bitmapSize.width, bitmapSize.height, hpr, vpr);
    });
  }

  // ── Shaded band between two moving edges, coloured per zone ────────────────
  private _drawCurveBands(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    hpr: number,
    vpr: number,
  ) {
    const bands = this._data.curveBands || [];
    if (bands.length === 0) return;

    for (const band of bands) {
      const n = Math.min(band.upper.length, band.lower.length);
      if (n < 2 || !band.segments || band.segments.length === 0) continue;

      // Project both edges once into media-pixel space, dropping any point the
      // chart cannot map (e.g. a time outside the loaded history).
      const xs = new Float64Array(n);
      const ysTop = new Float64Array(n);
      const ysBot = new Float64Array(n);
      const ts = new Float64Array(n);
      let m = 0;
      for (let i = 0; i < n; i++) {
        const x = this._x(band.upper[i].time, band.istOffset);
        if (x === null) continue;
        const yt = this._y(band.upper[i].value);
        if (yt === null) continue;
        const yb = this._y(band.lower[i].value);
        if (yb === null) continue;
        xs[m] = x;
        ysTop[m] = yt;
        ysBot[m] = yb;
        ts[m] = band.upper[i].time;
        m++;
      }
      if (m < 2) continue;

      const wantFill = band.fill !== false;
      const borderW = Math.max(0, band.borderWidth ?? 1);

      ctx.save();
      for (const seg of band.segments) {
        // One point of overlap on each side so the painted zone reaches its
        // boundary exactly instead of stopping a bar short.
        let i0 = lowerBound(ts, m, seg.fromTime);
        if (i0 > 0) i0--;
        let i1 = upperBound(ts, m, seg.toTime);
        if (i1 < m - 1) i1++;
        if (i1 <= i0) continue;

        const left = Math.max(0, xs[i0] * hpr);
        const right = Math.min(width, xs[i1] * hpr);
        if (right <= 0 || left >= width || right <= left) continue;

        // Cull zones that sit entirely above/below the visible price range.
        const yTop = Math.min(ysTop[i0], ysTop[i1]) * vpr;
        const yBot = Math.max(ysBot[i0], ysBot[i1]) * vpr;
        if (yBot < -2 || yTop > height + 2) continue;

        if (wantFill) {
          ctx.fillStyle = seg.color;
          ctx.beginPath();
          ctx.moveTo(xs[i0] * hpr, ysTop[i0] * vpr);
          for (let i = i0 + 1; i <= i1; i++) ctx.lineTo(xs[i] * hpr, ysTop[i] * vpr);
          for (let i = i1; i >= i0; i--) ctx.lineTo(xs[i] * hpr, ysBot[i] * vpr);
          ctx.closePath();
          ctx.fill();
        }

        const edge = seg.borderColor || band.border;
        if (edge && borderW > 0) {
          ctx.strokeStyle = edge;
          ctx.lineWidth = Math.max(1, Math.round(borderW * hpr));
          ctx.lineJoin = "round";
          ctx.beginPath();
          ctx.moveTo(xs[i0] * hpr, ysTop[i0] * vpr);
          for (let i = i0 + 1; i <= i1; i++) ctx.lineTo(xs[i] * hpr, ysTop[i] * vpr);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(xs[i0] * hpr, ysBot[i0] * vpr);
          for (let i = i0 + 1; i <= i1; i++) ctx.lineTo(xs[i] * hpr, ysBot[i] * vpr);
          ctx.stroke();
        }
      }
      ctx.restore();
    }
  }

  // ── Bounded horizontal reference lines (previous swing levels) ─────────────
  private _drawSegments(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    hpr: number,
    vpr: number,
  ) {
    const segments = this._data.segments || [];
    if (segments.length === 0) return;

    ctx.save();
    for (const seg of segments) {
      const x1 = this._x(seg.fromTime, seg.istOffset);
      if (x1 === null) continue;
      const x2 = this._x(seg.toTime, seg.istOffset);
      if (x2 === null) continue;
      const y = this._y(seg.price);
      if (y === null) continue;

      let left = Math.min(x1, x2) * hpr;
      let right = Math.max(x1, x2) * hpr;
      if (right <= 0 || left >= width) continue;
      left = Math.max(0, left);
      right = Math.min(width, right);

      const yy = Math.round(y * vpr) + 0.5;
      if (yy < -1 || yy > height + 1 || right - left < 1) continue;

      const lw = Math.max(1, Math.round((seg.lineWidth ?? 1) * hpr));
      const style = seg.lineStyle ?? 1;
      ctx.strokeStyle = seg.color;
      ctx.lineWidth = lw;
      ctx.lineCap = "butt";
      ctx.setLineDash(style === 1 ? [lw * 2, lw * 2] : style === 2 ? [lw * 6, lw * 3] : []);
      ctx.beginPath();
      ctx.moveTo(left, yy);
      ctx.lineTo(right, yy);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ── TradingView-style markers: boxed labels (LONG/SELL) and exit glyphs ────
  private _drawLabels(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    hpr: number,
    vpr: number,
  ) {
    const labels = this._data.labels || [];
    if (labels.length === 0) return;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // ── Phase 1: measure every marker ──────────────────────────────────────
    // Two levels anchored to nearby prices (an entry that coincides with a
    // stop, or two close targets) would otherwise stack on top of each other,
    // so each marker is measured up front and then nudged apart.
    interface Placed {
      label: LabelShape;
      cx: number;
      cy: number;
      left: number;
      w: number;
      top: number;
      h: number;
      half: number;
    }

    const items: Placed[] = [];
    for (const label of labels) {
      const x = this._x(label.time, label.istOffset);
      if (x === null) continue;
      const y = this._y(label.price);
      if (y === null) continue;
      const cx = x * hpr;
      const cy = y * vpr;

      if (label.shape === "diamond" || label.shape === "cross") {
        const half = ((label.size ?? 9) * vpr) / 2;
        const nudge = (label.offset ?? 0) * vpr;
        const gy = label.position === "aboveBar" ? cy - nudge : cy + nudge;
        if (cx + half < 0 || cx - half > width || gy + half < 0 || gy - half > height) continue;
        const halfW = half * 2;
        items.push({ label, cx, cy, left: clampLeft(cx - half, halfW, width), w: halfW, top: gy - half, h: halfW, half });
        continue;
      }

      const size = Math.max(7, label.size ?? 11);
      const fontPx = size * vpr;
      ctx.font = `700 ${fontPx}px Arial, Helvetica, sans-serif`;
      const padX = 5 * hpr;
      const padY = 3 * vpr;
      const boxW = ctx.measureText(label.text ?? "").width + padX * 2;
      const boxH = fontPx + padY * 2;
      // Right-edge level labels are centred on the newest bar, which is often
      // flush with the pane — keep the whole box inside the visible area.
      const left = clampLeft(cx - boxW / 2, boxW, width);
      if (left > width || left + boxW < 0) continue;

      const gap = (label.offset ?? 8) * vpr;
      const top = label.position === "aboveBar" ? cy - gap - boxH : cy + gap;
      if (top > height || top + boxH < 0) continue;
      items.push({ label, cx, cy, left, w: boxW, top, h: boxH, half: 0 });
    }

    if (items.length === 0) { ctx.restore(); return; }

    // ── Phase 2: resolve vertical overlaps ────────────────────────────────
    // Only markers whose horizontal spans actually intersect are separated, so
    // labels at different x (e.g. BUY below one bar, SELL above another) keep
    // their natural positions. Anything pushed below the pane is swept back up.
    const PAD = Math.max(1, Math.round(2 * vpr));
    const placed: Placed[] = [];
    const sweep = (candidates: Placed[], dir: 1 | -1) => {
      for (const it of candidates) {
        for (let guard = 0; guard < 64; guard++) {
          // Smallest move that clears every already-placed box it shares
          // horizontal space with; Infinity means nothing is in the way.
          let shift = Infinity;
          for (const p of placed) {
            if (it.left + it.w <= p.left + 1 || it.left >= p.left + p.w - 1) continue;
            const gapTop = p.top + p.h + PAD - it.top;
            const gapBottom = it.top + it.h + PAD - p.top;
            const want = gapTop >= 0 && gapTop < gapBottom ? gapTop : gapBottom;
            // `want === 0` means the PAD gap already holds — satisfied.
            if (want > 0 && want < shift) shift = want;
          }
          if (!isFinite(shift)) break;
          const next = it.top + dir * shift;
          if (next < -it.h || next > height + it.h) break;
          it.top = next;
        }
        placed.push(it);
      }
    };

    const ordered = [...items].sort((a, b) => a.top - b.top);
    sweep(ordered, 1);
    const overflowed = ordered.filter(it => it.top + it.h > height);
    if (overflowed.length > 0) {
      placed.length = 0;
      sweep([...items].sort((a, b) => b.top - a.top), -1);
    }

    // ── Phase 3: draw ─────────────────────────────────────────────────────
    for (const it of placed) {
      const label = it.label;
      const cx = it.cx;
      const cy = it.cy;

      // ── Exit glyphs: centred on the level, nudged by `offset` px ──
      if (label.shape === "diamond" || label.shape === "cross") {
        const half = it.half;
        const gy = it.top + half;
        ctx.fillStyle = label.color;
        ctx.strokeStyle = shade(label.color, 0.7);
        ctx.lineWidth = Math.max(1, Math.round(hpr));
        ctx.lineCap = "round";
        if (label.shape === "diamond") {
          ctx.beginPath();
          ctx.moveTo(cx, gy - half);
          ctx.lineTo(cx + half, gy);
          ctx.lineTo(cx, gy + half);
          ctx.lineTo(cx - half, gy);
          ctx.closePath();
          ctx.fill();
          if (label.border !== false) ctx.stroke();
        } else {
          // An X — two crossed strokes.
          const arm = half * 0.78;
          ctx.strokeStyle = label.color;
          ctx.lineWidth = Math.max(1.5, half * 0.42);
          ctx.beginPath();
          ctx.moveTo(cx - arm, gy - arm);
          ctx.lineTo(cx + arm, gy + arm);
          ctx.moveTo(cx + arm, gy - arm);
          ctx.lineTo(cx - arm, gy + arm);
          ctx.stroke();
        }
        continue;
      }

      // ── Boxed label ──
      const left = it.left;
      const top = it.top;
      roundRectPath(ctx, left, top, it.w, it.h, 2 * hpr);
      ctx.fillStyle = label.color;
      ctx.fill();
      if (label.border !== false) {
        ctx.strokeStyle = shade(label.color, 0.72);
        ctx.lineWidth = Math.max(1, Math.round(hpr));
        ctx.stroke();
      }

      ctx.fillStyle = label.textColor || "#FFFFFF";
      // Centre on the (possibly clamped) box, not the raw anchor.
      ctx.fillText(label.text ?? "", it.left + it.w / 2, top + it.h / 2);
    }
    ctx.restore();
  }
}

// ─── Primitive ────────────────────────────────────────────────────────────────
/** Two thin adapters so the same renderer can be mounted on two z-layers. */
class LayerView {
  private _renderer: IndicatorShapeRenderer;
  private _layer: "background" | "foreground";

  constructor(renderer: IndicatorShapeRenderer, layer: "background" | "foreground") {
    this._renderer = renderer;
    this._layer = layer;
  }

  renderer() {
    const renderer = this._renderer;
    const layer = this._layer;
    return {
      draw: (target: any) =>
        layer === "background"
          ? renderer.drawBackground(target)
          : renderer.drawForeground(target),
    };
  }

  zOrder(): "bottom" | "top" {
    return this._layer === "background" ? "bottom" : "top";
  }
}

export class IndicatorShapePrimitive implements ISeriesPrimitive<UTCTimestamp> {
  private _renderer: IndicatorShapeRenderer;
  private _bgView: LayerView;
  private _fgView: LayerView;
  private _requestUpdate: (() => void) | null = null;

  constructor(data: IndicatorShapeData, chart: IChartApi, series: any) {
    this._renderer = new IndicatorShapeRenderer(data, chart, series);
    this._bgView = new LayerView(this._renderer, "background");
    this._fgView = new LayerView(this._renderer, "foreground");
  }

  attached(param: any) {
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._requestUpdate = null;
  }

  updateAllViews() {}

  paneViews() {
    return [this._bgView, this._fgView] as any;
  }

  update(data: IndicatorShapeData) {
    this._renderer.update(data);
    this._requestUpdate?.();
  }
}